import { useCallback, useState } from 'react';

import { apiService } from '../services/api.services';
import { cleanCpf } from '../utils/cpfUtils';

export type ForgotUserkeyStep = 'cpf' | 'email';

interface UseForgotUserkeyReturn {
  isLoading: boolean;
  error: string | null;
  success: boolean;
  step: ForgotUserkeyStep;
  recoveredEmail: string | null;
  loadCorporateEmails: (cpf: string) => Promise<boolean>;
  sendUserkeyReset: (cpf: string) => Promise<boolean>;
  backToCpfStep: () => void;
  clearError: () => void;
  resetState: () => void;
}

export const useForgotUserkey = (): UseForgotUserkeyReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [step, setStep] = useState<ForgotUserkeyStep>('cpf');
  const [recoveredEmail, setRecoveredEmail] = useState<string | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const resetState = useCallback(() => {
    setError(null);
    setSuccess(false);
    setIsLoading(false);
    setStep('cpf');
    setRecoveredEmail(null);
  }, []);

  const backToCpfStep = useCallback(() => {
    setStep('cpf');
    setRecoveredEmail(null);
    setError(null);
    setSuccess(false);
  }, []);

  const loadCorporateEmails = useCallback(async (cpf: string): Promise<boolean> => {
    if (!cpf) {
      setError('CPF é obrigatório');
      return false;
    }

    const digits = cleanCpf(cpf);
    setIsLoading(true);
    setError(null);

    try {
      const userData = await apiService.userkeyMaintenance.postRecoverUser({
        cpf: digits,
      });

      const email = userData?.email?.trim();
      if (!email) {
        setError('Nenhum e-mail encontrado para este CPF.');
        return false;
      }

      setRecoveredEmail(email);
      setStep('email');
      return true;
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Não foi possível carregar os contatos. Tente novamente.');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const sendUserkeyReset = useCallback(async (cpf: string): Promise<boolean> => {
    if (!cpf) {
      setError('CPF é obrigatório');
      return false;
    }
    if (!recoveredEmail?.trim()) {
      setError('Nenhum e-mail encontrado para envio.');
      return false;
    }

    setIsLoading(true);
    setError(null);
    setSuccess(false);

    try {
      const digits = cleanCpf(cpf);
      await apiService.userkeyMaintenance.postForgetUserkey({
        cpf: digits,
        email: recoveredEmail.trim(),
        aplicacao: 'GIBS',
      });

      setSuccess(true);
      return true;
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('Erro ao enviar solicitação. Tente novamente.');
      }
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [recoveredEmail]);

  return {
    isLoading,
    error,
    success,
    step,
    recoveredEmail,
    loadCorporateEmails,
    sendUserkeyReset,
    backToCpfStep,
    clearError,
    resetState,
  };
};
